<!--
  AdminLogs.svelte — Admin audit log viewer.

  Uses the reusable AuditLogTable component for the main table display,
  adding filter controls, search, and a legend panel on top.
-->
<script lang="ts">
	import { Button, Badge, Input, Heading } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import { AbstractDatepicker } from '../../components';
	import { listAuditLogs } from '../../AppNetwork';
	import { onMount } from 'svelte';
	import type { AuditLog, AuditUserRef, SortDirection } from '../../AppTypes';
	import AuditLogTable from '../../components/AuditLogTable.svelte';
	import { t, t2 } from '../../i18n';

	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'gray' | 'primary';

	let logs: AuditLog[] = $state([]);
	let users: Record<number, AuditUserRef> = $state({});
	let loading = $state(true);
	let error = $state('');
	let total = $state(0);
	let page = $state(1);
	let limit = 25;
	let actionFilter = $state('');
	let searchQuery = $state('');
	/** Filter: date range lower bound. */
	let filterDateFrom = $state('');
	/** Filter: date range upper bound. */
	let filterDateTo = $state('');
	/** Active server-side sort key (`''` = server default, newest first). */
	let sortBy = $state('');
	/** Active server-side sort direction. */
	let sortDir: SortDirection = $state('desc');

	const actionTypes = [
		{ value: '', name: t2('All Actions'), icon: 'fa-solid fa-list' },
		{ value: 'USER_REGISTERED', name: t2('User Registered'), icon: 'fa-solid fa-user-plus' },
		{ value: 'USER_LOGIN', name: t2('User Login'), icon: 'fa-solid fa-right-to-bracket' },
		{ value: 'USER_UPDATED', name: t2('User Updated'), icon: 'fa-solid fa-user-pen' },
		{ value: 'USER_ACTIVATED', name: t2('User Activated'), icon: 'fa-solid fa-user-check' },
		{ value: 'USER_DEACTIVATED', name: t2('User Deactivated'), icon: 'fa-solid fa-user-xmark' },
		{ value: 'USER_DELETED', name: t2('User Deleted'), icon: 'fa-solid fa-user-minus' },
		{ value: 'USER_VERIFIED', name: t2('User Verified'), icon: 'fa-solid fa-user-shield' },
		{ value: 'PASSWORD_CHANGED', name: t2('Password Changed'), icon: 'fa-solid fa-key' },
		{ value: 'PASSWORD_RESET_REQUESTED', name: t2('Password Reset Requested'), icon: 'fa-solid fa-unlock-keyhole' },
		{ value: 'PASSWORD_RESET_COMPLETED', name: t2('Password Reset Completed'), icon: 'fa-solid fa-lock' },
		{ value: 'EMAIL_OTP_REQUESTED', name: t2('Email OTP Requested'), icon: 'fa-solid fa-envelope' },
		{ value: 'TWO_FACTOR_ENABLED', name: t2('2FA Enabled'), icon: 'fa-solid fa-shield-halved' },
		{ value: 'TWO_FACTOR_DISABLED', name: t2('2FA Disabled'), icon: 'fa-solid fa-shield' },
		{ value: 'SOCIAL_ACCOUNT_LINKED', name: t2('Social Account Linked'), icon: 'fa-solid fa-link' },
		{ value: 'LICENSE_SEATS_UPDATED', name: t2('License Seats Updated'), icon: 'fa-solid fa-user-group' },
		{ value: 'LICENSE_ACTIVATION_REVOKED', name: t2('License Activation Revoked'), icon: 'fa-solid fa-desktop' },
		{ value: 'SUPPORT_TICKET_CREATED', name: t2('Support Ticket Created'), icon: 'fa-solid fa-life-ring' },
		{ value: 'SUPPORT_TICKET_UPDATED', name: t2('Support Ticket Updated'), icon: 'fa-solid fa-headset' },
		{ value: 'SUPPORT_CONFIG_CHANGED', name: t2('Support Settings Changed'), icon: 'fa-solid fa-sliders' },
		{ value: 'ROLE_CHANGED', name: t2('Role Changed'), icon: 'fa-solid fa-user-tag' },
		{ value: 'ROLE_CREATED', name: t2('Role Created'), icon: 'fa-solid fa-plus' },
		{ value: 'ROLE_UPDATED', name: t2('Role Updated'), icon: 'fa-solid fa-pen' },
		{ value: 'ROLE_DELETED', name: t2('Role Deleted'), icon: 'fa-solid fa-trash' },
		{ value: 'SETTINGS_CHANGED', name: t2('Settings Changed'), icon: 'fa-solid fa-gear' },
		{ value: 'APPLICATION_CREATED', name: t2('Application Created'), icon: 'fa-solid fa-cube' },
		{ value: 'APPLICATION_UPDATED', name: t2('Application Updated'), icon: 'fa-solid fa-pen' },
		{ value: 'APPLICATION_DELETED', name: t2('Application Deleted'), icon: 'fa-solid fa-trash' },
		{ value: 'NEWSLETTER_CREATED', name: t2('Newsletter Created'), icon: 'fa-solid fa-newspaper' },
		{ value: 'NEWSLETTER_UPDATED', name: t2('Newsletter Updated'), icon: 'fa-solid fa-pen' },
		{ value: 'NEWSLETTER_DELETED', name: t2('Newsletter Deleted'), icon: 'fa-solid fa-trash' },
		{ value: 'NEWSLETTER_STARTED', name: t2('Newsletter Started'), icon: 'fa-solid fa-play' },
		{ value: 'NEWSLETTER_PAUSED', name: t2('Newsletter Paused'), icon: 'fa-solid fa-pause' },
		{ value: 'NEWSLETTER_RESUMED', name: t2('Newsletter Resumed'), icon: 'fa-solid fa-forward' },
		{ value: 'NEWSLETTER_SENT', name: t2('Newsletter Sent'), icon: 'fa-solid fa-paper-plane' },
		{ value: 'NEWSLETTER_CANCELLED', name: t2('Newsletter Cancelled'), icon: 'fa-solid fa-ban' }
	];

	const actionColors: Record<string, BadgeColor> = {
		USER_REGISTERED: 'green',
		USER_LOGIN: 'blue',
		USER_UPDATED: 'indigo',
		USER_ACTIVATED: 'green',
		USER_DEACTIVATED: 'red',
		USER_DELETED: 'red',
		USER_VERIFIED: 'green',
		PASSWORD_CHANGED: 'yellow',
		PASSWORD_RESET_REQUESTED: 'yellow',
		PASSWORD_RESET_COMPLETED: 'yellow',
		EMAIL_OTP_REQUESTED: 'blue',
		TWO_FACTOR_ENABLED: 'green',
		TWO_FACTOR_DISABLED: 'red',
		ROLE_CHANGED: 'purple',
		ROLE_CREATED: 'purple',
		ROLE_UPDATED: 'purple',
		ROLE_DELETED: 'red',
		SETTINGS_CHANGED: 'pink',
		APPLICATION_CREATED: 'indigo',
		APPLICATION_UPDATED: 'indigo',
		APPLICATION_DELETED: 'red',
		NEWSLETTER_CREATED: 'green',
		NEWSLETTER_UPDATED: 'indigo',
		NEWSLETTER_DELETED: 'red',
		NEWSLETTER_STARTED: 'blue',
		NEWSLETTER_PAUSED: 'yellow',
		NEWSLETTER_RESUMED: 'green',
		NEWSLETTER_SENT: 'blue',
		NEWSLETTER_CANCELLED: 'gray'
	};

	const actionIcons: Record<string, string> = {
		USER_REGISTERED: 'fa-user-plus',
		USER_LOGIN: 'fa-right-to-bracket',
		USER_UPDATED: 'fa-user-pen',
		USER_ACTIVATED: 'fa-user-check',
		USER_DEACTIVATED: 'fa-user-slash',
		USER_DELETED: 'fa-user-xmark',
		USER_VERIFIED: 'fa-circle-check',
		PASSWORD_CHANGED: 'fa-key',
		PASSWORD_RESET_REQUESTED: 'fa-envelope',
		PASSWORD_RESET_COMPLETED: 'fa-lock-open',
		EMAIL_OTP_REQUESTED: 'fa-envelope-open-text',
		TWO_FACTOR_ENABLED: 'fa-shield-halved',
		TWO_FACTOR_DISABLED: 'fa-shield',
		ROLE_CHANGED: 'fa-user-shield',
		ROLE_CREATED: 'fa-plus-circle',
		ROLE_UPDATED: 'fa-pen-to-square',
		ROLE_DELETED: 'fa-trash',
		SETTINGS_CHANGED: 'fa-gear',
		APPLICATION_CREATED: 'fa-cube',
		APPLICATION_UPDATED: 'fa-cubes',
		APPLICATION_DELETED: 'fa-cube',
		NEWSLETTER_CREATED: 'fa-plus-circle',
		NEWSLETTER_UPDATED: 'fa-pen-to-square',
		NEWSLETTER_DELETED: 'fa-trash',
		NEWSLETTER_STARTED: 'fa-play',
		NEWSLETTER_PAUSED: 'fa-pause',
		NEWSLETTER_RESUMED: 'fa-forward',
		NEWSLETTER_SENT: 'fa-paper-plane',
		NEWSLETTER_CANCELLED: 'fa-ban'
	};

	onMount(async () => {
		await loadLogs();
	});

	/**
	 *
	 */
	async function loadLogs() {
		loading = true;
		error = '';

		/** Fetch paginated audit logs with the active filter and update local state. */
		try {
			const offset = (page - 1) * limit;
			const params: Record<string, any> = { limit, offset };
			if (actionFilter) params.action = actionFilter;
			if (filterDateFrom) params.dateFrom = filterDateFrom;
			if (filterDateTo) params.dateTo = filterDateTo;
			if (sortBy) {
				params.sortBy = sortBy;
				params.sortDir = sortDir;
			}

			const result = await listAuditLogs(params);
			logs = result.items || [];
			total = result.total || 0;
			users = result.users || {};
		} catch (err: unknown) {
			error = err instanceof Error ? err.message : t2('Failed to load audit logs');
			console.error('Audit logs error:', err);
		} finally {
			loading = false;
		}
	}

	/**
	 *
	 */
	function handleFilterChange() {
		page = 1;
		loadLogs();
	}

	/**
	 *
	 */
	function handlePageChange(newPage: number) {
		page = newPage;
		loadLogs();
	}

	/** Switch the sort column/direction, reset to the first page, and refetch. */
	function handleSortChange(key: string, dir: SortDirection) {
		sortBy = key;
		sortDir = dir;
		page = 1;
		loadLogs();
	}

	let filteredLogs = $derived(searchQuery
		? logs.filter(
				(log) =>
					log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
					log.ipAddress?.toLowerCase().includes(searchQuery.toLowerCase())
			)
		: logs);
</script>

<Heading tag="h3">{$t("Audit Logs")}</Heading>

<div class="space-y-6">
	<!-- Filters -->
	<AbstractCard class="max-w-none">
		<div class="flex flex-wrap items-center gap-3">
			<div class="max-w-xs flex-1">
				<Input placeholder={$t("Search logs...")} bind:value={searchQuery}>
					{#snippet left()}
										<i  class="fa-solid fa-search text-gray-400"></i>
									{/snippet}
				</Input>
			</div>

			<AbstractDropdown items={actionTypes} bind:value={actionFilter} onchange={() => handleFilterChange()} class="w-48" />

			<div class="w-44">
				<AbstractDatepicker
					mode="date"
					bind:stringValue={filterDateFrom}
					showRelativeDate={false}
					onchange={handleFilterChange}
				/>
			</div>
			<span class="text-gray-400">&mdash;</span>
			<div class="w-44">
				<AbstractDatepicker
					mode="date"
					bind:stringValue={filterDateTo}
					showRelativeDate={false}
					onchange={handleFilterChange}
				/>
			</div>

			<div class="flex-1"></div>
			<Button color="alternative" onclick={loadLogs} disabled={loading}>
				{#if loading}
					<i class="fa-solid fa-spinner fa-spin"></i>
				{:else}
					<i class="fa-solid fa-refresh"></i>
				{/if}
			</Button>
		</div>
	</AbstractCard>

	{#if error}
		<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{error}
		</div>
	{/if}

	<!-- Logs Table (reusable component) -->
	<AbstractCard class="p-0 max-w-none">
		<AuditLogTable
			logs={filteredLogs}
			{users}
			{total}
			{limit}
			{page}
			{loading}
			{sortBy}
			{sortDir}
			onpagechange={handlePageChange}
			onsortchange={handleSortChange}
		/>
	</AbstractCard>

	<!-- Legend -->
	<AbstractCard class="max-w-none">
		<h4 class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">{$t("Action Types")}</h4>
		<div class="flex flex-wrap gap-3">
			{#each actionTypes.slice(1) as action (action.value)}
				<Badge color={actionColors[action.value] || 'gray'}>
					<i class="fa-solid {actionIcons[action.value] || 'fa-circle'} me-1"></i>
					{action.name}
				</Badge>
			{/each}
		</div>
	</AbstractCard>
</div>
